/**
 * Filtres du journal d'audit : modele, lecture de l'URL et requete serveur.
 *
 * Les filtres vivent **dans l'URL** (`?period=7d&action=...`), pas dans l'etat
 * du composant : c'est ce qui les conserve a l'ouverture du tiroir
 * (`useDetailRoute` garde la chaine de requete), apres un rechargement, et dans
 * un lien partage a un collegue. Module **pur**, teste sans rendu.
 *
 * Chaque filtre est execute par le serveur et son `total` le reflete : rien
 * n'est filtre ici, cote client.
 */

export type AuditPeriod = 'all' | '24h' | '7d' | '30d' | 'custom';

export interface AuditFilters {
  readonly period: AuditPeriod;
  /** Bornes d'une periode `custom`, en ISO 8601 a la seconde ; `''` = non borne. */
  readonly from: string;
  readonly to: string;
  readonly action: string;
  readonly targetType: string;
  readonly targetId: string;
  readonly actorUserId: string;
  readonly correlationId: string;
  /** Vrai : `excludeAction=admin.access`, les consultations sont masquees. */
  readonly hideAccess: boolean;
}

/** Sept jours : ce que la maquette ouvre par defaut, et ce qui borne le cout du `COUNT(*)`. */
export const DEFAULT_PERIOD: AuditPeriod = '7d';

export const DEFAULT_FILTERS: AuditFilters = {
  period: DEFAULT_PERIOD,
  from: '',
  to: '',
  action: '',
  targetType: '',
  targetId: '',
  actorUserId: '',
  correlationId: '',
  hideAccess: false,
};

/** Action ecartee par « Masquer les consultations ». */
export const ACCESS_ACTION = 'admin.access';

export const PERIOD_OPTIONS: readonly { readonly value: AuditPeriod; readonly label: string }[] = [
  { value: '24h', label: '24 dernières heures' },
  { value: '7d', label: '7 derniers jours' },
  { value: '30d', label: '30 derniers jours' },
  { value: 'all', label: 'Toute la période' },
  { value: 'custom', label: 'Dates précises' },
];

const PERIOD_MILLISECONDS: Readonly<Partial<Record<AuditPeriod, number>>> = {
  '24h': 24 * 3_600_000,
  '7d': 7 * 24 * 3_600_000,
  '30d': 30 * 24 * 3_600_000,
};

const isPeriod = (value: string | null): value is AuditPeriod =>
  value === 'all' || value === '24h' || value === '7d' || value === '30d' || value === 'custom';

/** Page demandee : un entier superieur ou egal a 1, sinon 1. */
export const readPage = (params: URLSearchParams): number => {
  const parsed = Number.parseInt(params.get('page') ?? '', 10);

  return Number.isInteger(parsed) && parsed >= 1 ? parsed : 1;
};

export const readFilters = (params: URLSearchParams): AuditFilters => {
  const period = params.get('period');

  return {
    period: isPeriod(period) ? period : DEFAULT_PERIOD,
    from: params.get('from') ?? '',
    to: params.get('to') ?? '',
    action: params.get('action') ?? '',
    targetType: params.get('targetType') ?? '',
    targetId: params.get('targetId') ?? '',
    actorUserId: params.get('actorUserId') ?? '',
    correlationId: params.get('correlationId') ?? '',
    hideAccess: params.get('hideAccess') === '1',
  };
};

/**
 * Chaine de requete de l'URL de l'ecran. Une valeur par defaut n'y figure pas :
 * l'URL « nue » est celle de l'ouverture, et un lien ne porte que ce qui change.
 * Les bornes ne sont ecrites que pour une periode `custom`, ou elles ont un sens.
 */
export const writeFilters = (filters: AuditFilters, page = 1): URLSearchParams => {
  const params = new URLSearchParams();

  if (filters.period !== DEFAULT_PERIOD) {
    params.set('period', filters.period);
  }

  if (filters.period === 'custom') {
    if (filters.from !== '') {
      params.set('from', filters.from);
    }

    if (filters.to !== '') {
      params.set('to', filters.to);
    }
  }

  for (const key of ['action', 'targetType', 'targetId', 'actorUserId', 'correlationId'] as const) {
    if (filters[key] !== '') {
      params.set(key, filters[key]);
    }
  }

  if (filters.hideAccess) {
    params.set('hideAccess', '1');
  }

  if (page > 1) {
    params.set('page', String(page));
  }

  return params;
};

/**
 * Instant au format que le serveur lit sans surprise : ISO 8601, **a la
 * seconde**, en UTC (`2026-10-04T08:42:07Z`). `Date#toISOString` ajoute des
 * millisecondes (`.000`) ; le journal ne garde pas les fractions de seconde et
 * les fixer ici evite qu'une borne « 08:42:07.999 » ne s'ecarte de ce que
 * l'ecran montre.
 */
export const toIsoSeconds = (date: Date): string => date.toISOString().replace(/\.\d{3}Z$/, 'Z');

/**
 * Valeur d'un `<input type="datetime-local">` (heure locale, a la minute) vers
 * l'instant ISO du serveur. `endOfMinute` : pour une borne haute **inclusive**,
 * « 18:30 » doit couvrir les evenements de 18:30:42, pas s'arreter a 18:30:00.
 * Une saisie illisible rend `''` : pas de borne plutot qu'une borne inventee.
 */
export const localInputToIso = (value: string, endOfMinute = false): string => {
  if (value === '') {
    return '';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  if (endOfMinute) {
    date.setSeconds(59);
  }

  return toIsoSeconds(date);
};

const pad = (value: number): string => String(value).padStart(2, '0');

/** Instant ISO vers la valeur d'un `<input type="datetime-local">` (heure locale). `''` si illisible. */
export const isoToLocalInput = (iso: string): string => {
  const date = new Date(iso);

  if (iso === '' || Number.isNaN(date.getTime())) {
    return '';
  }

  return `${String(date.getFullYear())}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

/**
 * Bornes de la periode. Une periode relative est ancree sur `now`, **lu a
 * chaque chargement** : « Actualiser » deplace donc la fenetre, ce qu'une borne
 * figee dans l'URL ne ferait pas.
 */
export const periodBounds = (
  filters: AuditFilters,
  now: Date,
): { readonly from: string; readonly to: string } => {
  if (filters.period === 'custom') {
    return { from: filters.from, to: filters.to };
  }

  const span = PERIOD_MILLISECONDS[filters.period];

  return span === undefined
    ? { from: '', to: '' }
    : { from: toIsoSeconds(new Date(now.getTime() - span)), to: '' };
};

/**
 * Parametres de requete de `GET /admin/audit-events` (et de l'export, qui
 * reprend les memes). Seuls les filtres renseignes sont envoyes.
 */
export const toQuery = (filters: AuditFilters, now: Date): Record<string, string> => {
  const { from, to } = periodBounds(filters, now);
  const query: Record<string, string> = {};

  const set = (key: string, value: string): void => {
    if (value !== '') {
      query[key] = value;
    }
  };

  set('from', from);
  set('to', to);
  set('action', filters.action);
  set('targetType', filters.targetType);
  set('targetId', filters.targetId);
  set('actorUserId', filters.actorUserId);
  set('correlationId', filters.correlationId);

  if (filters.hideAccess) {
    query.excludeAction = ACCESS_ACTION;
  }

  return query;
};

/** Un filtre differe-t-il de l'ouverture ? Pilote « Réinitialiser les filtres ». */
export const hasActiveFilter = (filters: AuditFilters): boolean => writeFilters(filters).size > 0;
